import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { APP_NAME } from "../helpers/constants";

/**
 * Explains Scale-tier capabilities when a lower tier hits a locked feature.
 */
export default function ScaleUpgradeModal({ open, onClose }) {
  const navigate = useNavigate();

  return (
    <BrandedModal
      title={
        <ModalTitleRow>
          <IconWrap aria-hidden>
            <LockOutlined />
          </IconWrap>
          <TitleText>Upgrade to Scale</TitleText>
        </ModalTitleRow>
      }
      open={open}
      onCancel={onClose}
      centered
      width={480}
      footer={
        <FooterRow>
          <GhostButton onClick={onClose}>Not now</GhostButton>
          <PrimaryButton
            type="primary"
            onClick={() => {
              onClose();
              navigate("/profile#subscription-billing");
            }}
          >
            View billing and upgrade
          </PrimaryButton>
        </FooterRow>
      }
      closeIcon={<CloseIcon />}
    >
      <BodyCopy>
        <Lead>
          Higher concurrency, deeper vendor tooling, and priority support ship on
          the <strong>Scale</strong> tier of {APP_NAME}, built for teams running
          many concurrent jobs.
        </Lead>
        <p>
          When you&apos;re ready to unlock Scale, open your{" "}
          <strong>Profile</strong> and go to <strong>Billing</strong>.
        </p>
      </BodyCopy>
    </BrandedModal>
  );
}

const BrandedModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow:
      0 25px 50px -12px rgba(30, 77, 30, 0.18),
      0 0 0 1px rgba(44, 95, 45, 0.08);
  }

  .ant-modal-header {
    margin: 0;
    padding: 1.125rem 1.25rem 1.125rem 1.25rem;
    background: linear-gradient(
      135deg,
      var(--color-primary-dark) 0%,
      var(--color-primary) 55%,
      var(--color-primary-light) 160%
    );
    border-bottom: none;
  }

  .ant-modal-title {
    margin: 0;
  }

  .ant-modal-close {
    top: 0.875rem;
    inset-inline-end: 0.875rem;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.92) !important;
    background: transparent;

    .anticon,
    .anticon svg,
    svg {
      color: rgba(255, 255, 255, 0.92) !important;
      fill: rgba(255, 255, 255, 0.92) !important;
    }

    &:hover {
      color: #fff !important;
      background: rgba(255, 255, 255, 0.14) !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: #fff !important;
      fill: #fff !important;
    }

    &:focus-visible {
      outline: 2px solid rgba(255, 255, 255, 0.85);
      outline-offset: 2px;
    }
  }

  .ant-modal-body {
    padding: 1.375rem 1.5rem 1.5rem;
    background: #fff;
  }

  .ant-modal-footer {
    margin: 0;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid rgba(44, 95, 45, 0.1);
    background: linear-gradient(
      180deg,
      var(--color-primary-glow, rgba(44, 95, 45, 0.06)) 0%,
      #fafcfb 100%
    );
  }
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 18px;
  flex-shrink: 0;
`;

const TitleText = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #fff;
  line-height: 1.3;
`;

function CloseIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 1L13 13M13 1L1 13"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const BodyCopy = styled.div`
  color: #334155;
  font-size: 0.9375rem;
  line-height: 1.6;

  p {
    margin: 0 0 0.875rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: var(--color-primary-dark, #1e4d1e);
    font-weight: 600;
  }
`;

const Lead = styled.p`
  margin: 0 0 1rem !important;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  border-left: 4px solid var(--color-primary);
  background: var(--color-primary-glow, rgba(44, 95, 45, 0.08));
`;

const FooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.625rem;
  width: 100%;
`;

const GhostButton = styled(Button)`
  border-color: rgba(44, 95, 45, 0.35);
  color: var(--color-primary-dark, #1e4d1e);
  font-weight: 500;

  &:hover {
    border-color: var(--color-primary) !important;
    color: var(--color-primary) !important;
    background: var(--color-primary-glow, rgba(44, 95, 45, 0.08)) !important;
  }
`;

const PrimaryButton = styled(Button)`
  font-weight: 600;
  min-width: 11rem;
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.25);

  &.ant-btn-primary {
    background: var(--color-primary);
    border-color: var(--color-primary);
  }

  &.ant-btn-primary:hover {
    background: var(--color-primary-dark) !important;
    border-color: var(--color-primary-dark) !important;
  }
`;
